// frontend/src/components/public/blocks/MetricsBlock.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@mui/material';
import { getIconByValue } from '../../../constants/metricIcons';

interface MetricsBlockProps {
  data: {
    metrics: Array<{
      label: string;
      value: number | string;
      format?: 'number' | 'currency' | 'percentage';
      icon?: string;
      source?: string;
      context?: string;
    }>;
    layout: 'grid' | 'row';
    enableAnimations?: boolean;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const MetricsBlock: React.FC<MetricsBlockProps> = ({ data, theme }) => {
  const { metrics = [], layout = 'grid', enableAnimations = true } = data;
  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';

  const [hasAnimated, setHasAnimated] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enableAnimations) {
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (blockRef.current) {
      observer.observe(blockRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, enableAnimations]);

  const formatValue = (value: number | string, format?: string) => {
    // Convert string to number if needed
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    // If conversion failed or value is actually non-numeric text, return as-is
    if (isNaN(numValue)) return value;

    switch (format) {
      case 'currency':
        return `€${numValue.toLocaleString()}`;
      case 'percentage':
        return `${numValue}%`;
      case 'number':
      default:
        return numValue.toLocaleString();
    }
  };

  if (metrics.length === 0) {
    return null;
  }

  // Grid Layout
  if (layout === 'grid') {
    return (
      <Box
        id="metrics"
        ref={blockRef}
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        <Grid container spacing={4}>
          {metrics.map((metric, index) => {
            const numValue = typeof metric.value === 'string' ? parseFloat(metric.value) : metric.value;
            const isNumeric = !isNaN(numValue);

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={metrics.length === 2 ? 6 : metrics.length === 3 ? 4 : 3}
                key={index}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 32px ${accentColor}22`,
                      borderColor: accentColor
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      bgcolor: accentColor,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover::before': {
                      transform: 'scaleX(1)'
                    }
                  }}
                >
                  <CardContent
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      minHeight: '180px'
                    }}
                  >
                    {/* ICON RENDERING */}
                    {metric.icon && (() => {
                      const iconData = getIconByValue(metric.icon);
                      if (iconData) {
                        const IconComponent = iconData.icon;
                        return (
                          <Box sx={{ mb: 2, color: accentColor, display: 'flex', justifyContent: 'center' }}>
                            <IconComponent />
                          </Box>
                        );
                      }
                      return null;
                    })()}

                    {isNumeric && enableAnimations ? (
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { xs: '2.5rem', md: '3rem' },
                          fontWeight: 800,
                          color: primaryColor,
                          mb: 1,
                          lineHeight: 1
                        }}
                      >
                        <AnimatedNumber
                          value={numValue}
                          animate={hasAnimated}
                          duration={2000}
                          format={metric.format}
                        />
                      </Typography>
                    ) : (
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { xs: '2.5rem', md: '3rem' },
                          fontWeight: 800,
                          color: primaryColor,
                          mb: 1,
                          lineHeight: 1
                        }}
                      >
                        {formatValue(metric.value, metric.format)}
                      </Typography>
                    )}

                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: '1rem', md: '1.125rem' },
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: metric.source || metric.context ? 1 : 0
                      }}
                    >
                      {metric.label}
                    </Typography>

                    {metric.source && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          mt: 0.5
                        }}
                      >
                        Source: {metric.source}
                      </Typography>
                    )}

                    {metric.context && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                          mt: 1
                        }}
                      >
                        {metric.context}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }

  // Row Layout
  return (
    <Box
      ref={blockRef}
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 3, md: 6 },
        bgcolor: '#f5f5f5'
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-around',
          gap: { xs: 3, sm: 2 }
        }}
      >
        {metrics.map((metric, index) => {
          const numValue = typeof metric.value === 'string' ? parseFloat(metric.value) : metric.value;
          const isNumeric = !isNaN(numValue);

          return (
            <Box
              key={index}
              sx={{
                textAlign: 'center',
                flex: 1,
                py: 2,
                px: 3,
                position: 'relative',
                '&:not(:last-child)::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '60%',
                  width: '1px',
                  bgcolor: '#e0e0e0',
                  display: { xs: 'none', sm: 'block' }
                }
              }}
            >
              {/* ICON RENDERING */}
              {metric.icon && (() => {
                const iconData = getIconByValue(metric.icon);
                if (iconData) {
                  const IconComponent = iconData.icon;
                  return (
                    <Box sx={{ mb: 1, color: accentColor, display: 'flex', justifyContent: 'center' }}>
                      <IconComponent />
                    </Box>
                  );
                }
                return null;
              })()}

              {isNumeric && enableAnimations ? (
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 800,
                    color: primaryColor,
                    mb: 0.5
                  }}
                >
                  <AnimatedNumber
                    value={numValue}
                    animate={hasAnimated}
                    duration={2000}
                    format={metric.format}
                  />
                </Typography>
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 800,
                    color: primaryColor,
                    mb: 0.5
                  }}
                >
                  {formatValue(metric.value, metric.format)}
                </Typography>
              )}

              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {metric.label}
              </Typography>

              {metric.source && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    mt: 0.5
                  }}
                >
                  Source: {metric.source}
                </Typography>
              )}

              {metric.context && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    mt: 0.5
                  }}
                >
                  {metric.context}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Animated Number Component
interface AnimatedNumberProps {
  value: number;
  animate: boolean;
  duration?: number;
  format?: 'number' | 'currency' | 'percentage';
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  animate,
  duration = 2000,
  format = 'number'
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!animate) {
      setDisplayValue(value);
      return;
    }

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateValue = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(easeOutQuart * value);
      
      setDisplayValue(current);

      if (now < endTime) {
        requestAnimationFrame(updateValue);
      } else {
        setDisplayValue(value);
      }
    };

    requestAnimationFrame(updateValue);
  }, [animate, value, duration]);

  const formatDisplay = (val: number) => {
    switch (format) {
      case 'currency':
        return `€${val.toLocaleString()}`;
      case 'percentage':
        return `${val}%`;
      case 'number':
      default:
        return val.toLocaleString();
    }
  };

  return <>{formatDisplay(displayValue)}</>;
};

export default MetricsBlock;